import { useState, useEffect, useRef } from 'react';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Star, StarOff, 
  Save, X, Image, Upload, Search,
  Settings, FileText, Palette, Globe, ArrowUp, ArrowDown,
  Bold, Italic, List, ListOrdered, Link, Heading1, Heading2, Heading3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Color options for categories
const CATEGORY_COLORS = [
  { name: 'Kırmızı', value: 'red', gradient: 'from-red-600 to-orange-500', bg: 'bg-red-100', text: 'text-red-700' },
  { name: 'Mavi', value: 'blue', gradient: 'from-blue-600 to-cyan-500', bg: 'bg-blue-100', text: 'text-blue-700' },
  { name: 'Yeşil', value: 'green', gradient: 'from-green-600 to-emerald-500', bg: 'bg-green-100', text: 'text-green-700' },
  { name: 'Mor', value: 'purple', gradient: 'from-purple-600 to-pink-500', bg: 'bg-purple-100', text: 'text-purple-700' },
  { name: 'Sarı', value: 'yellow', gradient: 'from-yellow-500 to-orange-500', bg: 'bg-yellow-100', text: 'text-yellow-700' },
  { name: 'İndigo', value: 'indigo', gradient: 'from-indigo-600 to-violet-500', bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { name: 'Teal', value: 'teal', gradient: 'from-teal-600 to-cyan-500', bg: 'bg-teal-100', text: 'text-teal-700' },
  { name: 'Gri', value: 'gray', gradient: 'from-gray-600 to-slate-500', bg: 'bg-gray-100', text: 'text-gray-700' },
];

export default function BlogAdminTab({ adminPassword }) {
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('posts');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);
  const [showQuickPreview, setShowQuickPreview] = useState(false);
  const [quickPreviewPost, setQuickPreviewPost] = useState(null);

  useEffect(() => {
    fetchPosts();
    fetchSettings();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/admin/blog/posts`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      const data = await response.json();
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/blog/settings`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching blog settings:', error);
    }
  };

  const handleSavePost = async (postData) => {
    try {
      const url = editingPost?.id 
        ? `${API}/admin/blog/posts/${editingPost.id}`
        : `${API}/admin/blog/posts`;
      
      const method = editingPost?.id ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword
        },
        body: JSON.stringify(postData)
      });
      
      const data = await response.json();
      if (data.success) {
        fetchPosts();
        setShowEditModal(false);
        setEditingPost(null);
      } else {
        alert(data.message || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Kayıt sırasında hata oluştu');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Bu blog yazısını silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API}/admin/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': adminPassword }
      });
      const data = await response.json();
      if (data.success) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleTogglePublish = async (postId) => {
    try {
      const response = await fetch(`${API}/admin/blog/posts/${postId}/toggle-publish`, {
        method: 'PUT',
        headers: { 'X-Admin-Password': adminPassword }
      });
      const data = await response.json();
      if (data.success) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const handleToggleFeatured = async (postId) => {
    try {
      const response = await fetch(`${API}/admin/blog/posts/${postId}/toggle-featured`, {
        method: 'PUT',
        headers: { 'X-Admin-Password': adminPassword }
      });
      const data = await response.json();
      if (data.success) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/blog/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword
        },
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      if (data.success) {
        alert('Blog ayarları kaydedildi!');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const handleMovePost = async (postId, direction) => {
    const currentIndex = posts.findIndex(p => p.id === postId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= posts.length) return;
    
    const newPosts = [...posts];
    [newPosts[currentIndex], newPosts[newIndex]] = [newPosts[newIndex], newPosts[currentIndex]];
    
    try {
      await fetch(`${API}/admin/blog/posts/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword
        },
        body: JSON.stringify(newPosts.map((p, i) => ({ id: p.id, order: i })))
      });
      fetchPosts();
    } catch (error) {
      console.error('Error reordering posts:', error);
    }
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getColorConfig = (colorValue) => {
    return CATEGORY_COLORS.find(c => c.value === colorValue) || CATEGORY_COLORS[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📰 Blog Yönetimi</h2>
          <p className="text-gray-600">Blog yazılarını ekleyin, düzenleyin ve yönetin</p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setActiveView('posts')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeView === 'posts' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Yazılar ({posts.length})
          </button>
          <button
            onClick={() => setActiveView('settings')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeView === 'settings' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Ayarlar
          </button>
        </div>
      </div>

      {activeView === 'posts' && (
        <>
          {/* Actions Bar */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-4 rounded-xl shadow-sm">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Yazı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            <button
              onClick={() => {
                setEditingPost(null);
                setShowEditModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Yeni Yazı Ekle
            </button>
          </div>

          {/* Posts List */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sıra</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Yazı</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Kategori</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Durum</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Öne Çıkan</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPosts.map((post, index) => {
                    const colorConfig = getColorConfig(post.category_color);
                    return (
                      <tr 
                        key={post.id} 
                        className="hover:bg-blue-50 transition-colors cursor-pointer"
                        onClick={() => {
                          setQuickPreviewPost(post);
                          setShowQuickPreview(true);
                        }}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleMovePost(post.id, 'up')}
                              disabled={index === 0}
                              className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleMovePost(post.id, 'down')}
                              disabled={index === filteredPosts.length - 1}
                              className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </button>
                            <span className="text-gray-500 text-sm ml-1">{index + 1}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{post.emoji}</span>
                            <div>
                              <p className="font-medium text-gray-900 line-clamp-1">{post.title}</p>
                              <p className="text-xs text-gray-500">/blog/{post.slug}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorConfig.bg} ${colorConfig.text}`}>
                            {post.category}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleTogglePublish(post.id)}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              post.is_published 
                                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {post.is_published ? (
                              <><Eye className="w-3 h-3 inline mr-1" />Yayında</>
                            ) : (
                              <><EyeOff className="w-3 h-3 inline mr-1" />Taslak</>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleToggleFeatured(post.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              post.is_featured 
                                ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200' 
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                          >
                            {post.is_featured ? <Star className="w-5 h-5 fill-current" /> : <StarOff className="w-5 h-5" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setPreviewPost(post);
                                setShowPreview(true);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Önizle"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingPost(post);
                                setShowEditModal(true);
                              }}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Düzenle"
                            >
                              <Edit2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeletePost(post.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Sil"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {filteredPosts.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Henüz blog yazısı yok</p>
                <button
                  onClick={() => {
                    setEditingPost(null);
                    setShowEditModal(true);
                  }}
                  className="mt-4 text-red-600 hover:underline"
                >
                  İlk yazıyı ekle
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {activeView === 'settings' && settings && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Blog Ayarları
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="w-4 h-4 inline mr-2" />
                Bölüm Başlığı
              </label>
              <input
                type="text"
                value={settings.section_title}
                onChange={(e) => setSettings({...settings, section_title: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bölüm Alt Başlığı
              </label>
              <input
                type="text"
                value={settings.section_subtitle}
                onChange={(e) => setSettings({...settings, section_subtitle: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ana Sayfada Gösterilecek Yazı Sayısı
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.homepage_post_count}
                onChange={(e) => setSettings({...settings, homepage_post_count: parseInt(e.target.value)})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sayfa Başına Yazı Sayısı
              </label>
              <input
                type="number"
                min="3"
                max="20"
                value={settings.posts_per_page}
                onChange={(e) => setSettings({...settings, posts_per_page: parseInt(e.target.value)})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.show_on_homepage}
                onChange={(e) => setSettings({...settings, show_on_homepage: e.target.checked})}
                className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Ana Sayfada Göster</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_search}
                onChange={(e) => setSettings({...settings, enable_search: e.target.checked})}
                className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Arama Aktif</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_categories}
                onChange={(e) => setSettings({...settings, enable_categories: e.target.checked})}
                className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Kategoriler Aktif</span>
            </label>
          </div>
          
          <hr className="my-6" />
          
          <h4 className="font-bold text-gray-900">CTA (Call to Action) Ayarları</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CTA Başlığı</label>
              <input
                type="text"
                value={settings.cta_title}
                onChange={(e) => setSettings({...settings, cta_title: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CTA Buton Metni</label>
              <input
                type="text"
                value={settings.cta_button_text}
                onChange={(e) => setSettings({...settings, cta_button_text: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">CTA Alt Başlığı</label>
              <input
                type="text"
                value={settings.cta_subtitle}
                onChange={(e) => setSettings({...settings, cta_subtitle: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Save className="w-5 h-5" />
              Ayarları Kaydet
            </button>
          </div>
        </div>
      )}

      {/* Quick Preview Modal - Opens on row click */}
      {showQuickPreview && quickPreviewPost && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
          onClick={() => setShowQuickPreview(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${quickPreviewPost.gradient || 'from-red-600 to-orange-500'} p-5`}>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  <span className="text-4xl">{quickPreviewPost.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-white">{quickPreviewPost.title}</h2>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-0.5 bg-white/20 rounded text-xs text-white">
                        {quickPreviewPost.category}
                      </span>
                      <span className="text-white/70 text-xs">
                        {quickPreviewPost.is_published ? '✅ Yayında' : '📝 Taslak'}
                      </span>
                      {quickPreviewPost.is_featured && (
                        <span className="text-yellow-300 text-xs">⭐ Öne Çıkan</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowQuickPreview(false)}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5 overflow-y-auto max-h-[50vh]">
              {quickPreviewPost.image_url && (
                <img 
                  src={quickPreviewPost.image_url.startsWith('/') ? `${API_URL}${quickPreviewPost.image_url}` : quickPreviewPost.image_url}
                  alt=""
                  className="w-full h-40 object-cover rounded-xl mb-4"
                />
              )}
              {quickPreviewPost.excerpt && (
                <p className="text-gray-600 text-sm mb-4 italic">{quickPreviewPost.excerpt}</p>
              )}
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: quickPreviewPost.content }}
              />
            </div>
            
            {/* Footer actions */}
            <div className="px-5 py-3 bg-gray-50 border-t flex justify-between items-center">
              <div className="text-xs text-gray-500">
                Slug: <span className="font-mono">/blog/{quickPreviewPost.slug}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowQuickPreview(false);
                    setEditingPost(quickPreviewPost);
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1"
                >
                  <Edit2 className="w-4 h-4" />
                  Düzenle
                </button>
                <a
                  href={`/blog/${quickPreviewPost.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 flex items-center gap-1"
                >
                  <Globe className="w-4 h-4" />
                  Sayfayı Aç
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <BlogPostEditor
          post={editingPost}
          onSave={handleSavePost}
          onClose={() => {
            setShowEditModal(false);
            setEditingPost(null);
          }}
          adminPassword={adminPassword}
        />
      )}

      {/* Preview Modal - Enhanced */}
      {showPreview && previewPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className={`bg-gradient-to-r ${previewPost.gradient || 'from-red-600 to-orange-500'} p-6`}>
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-5xl">{previewPost.emoji}</span>
                  <h2 className="text-2xl font-bold text-white mt-3">{previewPost.title}</h2>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm text-white font-medium">
                      {previewPost.category}
                    </span>
                    <span className="text-white/70 text-sm">
                      {previewPost.is_published ? '✅ Yayında' : '📝 Taslak'}
                    </span>
                    {previewPost.is_featured && (
                      <span className="text-yellow-300 text-sm">⭐ Öne Çıkan</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {previewPost.image_url && (
                <img 
                  src={previewPost.image_url.startsWith('/') ? `${API_URL}${previewPost.image_url}` : previewPost.image_url}
                  alt=""
                  className="w-full h-48 object-cover rounded-xl mb-6"
                />
              )}
              {previewPost.excerpt && (
                <p className="text-lg text-gray-600 mb-6 italic border-l-4 border-red-500 pl-4">
                  {previewPost.excerpt}
                </p>
              )}
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: previewPost.content }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Blog Post Editor Component - Visual Editor (WYSIWYG)
function BlogPostEditor({ post, onSave, onClose, adminPassword }) {
  const [formData, setFormData] = useState({
    slug: post?.slug || '',
    title: post?.title || '',
    category: post?.category || 'REHBER',
    category_color: post?.category_color || 'red',
    emoji: post?.emoji || '📝',
    gradient: post?.gradient || 'from-red-600 to-orange-500',
    image_url: post?.image_url || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '<p>Buraya içerik yazın...</p>',
    is_published: post?.is_published ?? true,
    is_featured: post?.is_featured ?? false,
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
  });
  
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  // Initialize editor content when component mounts
  useEffect(() => {
    if (editorRef.current && !initializedRef.current) {
      editorRef.current.innerHTML = formData.content;
      initializedRef.current = true;
    }
  }, [formData.content]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'title' && !post?.id) {
      const slug = value
        .toLowerCase()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
    
    if (field === 'category_color') {
      const colorConfig = CATEGORY_COLORS.find(c => c.value === value);
      if (colorConfig) {
        setFormData(prev => ({ ...prev, gradient: colorConfig.gradient }));
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API}/admin/blog/upload-image`, {
        method: 'POST',
        headers: { 'X-Admin-Password': adminPassword },
        body: formDataUpload
      });
      
      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, image_url: data.url }));
      } else {
        alert(data.message || 'Yükleme hatası');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Resim yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  // Rich text editor commands
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    // Update content after command
    if (editorRef.current) {
      handleChange('content', editorRef.current.innerHTML);
    }
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      handleChange('content', editorRef.current.innerHTML);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Başlık gerekli');
      return;
    }
    if (!formData.slug.trim()) {
      alert('URL slug gerekli');
      return;
    }
    if (!formData.content.trim() || formData.content === '<p>Buraya içerik yazın...</p>') {
      alert('İçerik gerekli');
      return;
    }
    
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h2 className="text-xl font-bold text-gray-900">
            {post?.id ? '✏️ Yazıyı Düzenle' : '➕ Yeni Yazı Ekle'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('content')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'content' 
                ? 'text-red-600 border-b-2 border-red-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            İçerik
          </button>
          <button
            onClick={() => setActiveTab('style')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'style' 
                ? 'text-red-600 border-b-2 border-red-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Palette className="w-4 h-4 inline mr-2" />
            Görünüm
          </button>
          <button
            onClick={() => setActiveTab('seo')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'seo' 
                ? 'text-red-600 border-b-2 border-red-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Globe className="w-4 h-4 inline mr-2" />
            SEO
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {activeTab === 'content' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlık *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Blog yazısı başlığı"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL Slug *
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 bg-gray-100 border border-r-0 rounded-l-lg text-gray-500 text-sm">
                      /blog/
                    </span>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => handleChange('slug', e.target.value)}
                      className="flex-1 px-4 py-2 border rounded-r-lg focus:ring-2 focus:ring-red-500"
                      placeholder="yazi-url"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kısa Açıklama (Excerpt)
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => handleChange('excerpt', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Blog listesinde görünecek kısa açıklama..."
                />
              </div>

              {/* Visual Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  İçerik * (Görsel Editör)
                </label>
                
                {/* Toolbar */}
                <div className="flex flex-wrap gap-1 p-2 bg-gray-100 border border-b-0 rounded-t-lg">
                  <button
                    type="button"
                    onClick={() => execCommand('formatBlock', 'h2')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Büyük Başlık"
                  >
                    <Heading1 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('formatBlock', 'h3')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Küçük Başlık"
                  >
                    <Heading2 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('formatBlock', 'p')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Paragraf"
                  >
                    <Heading3 className="w-4 h-4" />
                  </button>
                  <div className="w-px bg-gray-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => execCommand('bold')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors font-bold"
                    title="Kalın"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('italic')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors italic"
                    title="İtalik"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <div className="w-px bg-gray-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => execCommand('insertUnorderedList')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Madde İşaretli Liste"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('insertOrderedList')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Numaralı Liste"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </button>
                  <div className="w-px bg-gray-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => {
                      const url = prompt('Link URL:');
                      if (url) execCommand('createLink', url);
                    }}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Link Ekle"
                  >
                    <Link className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Editable Content */}
                <div
                  ref={(el) => {
                    editorRef.current = el;
                    // Set initial content only once
                    if (el && !el.dataset.initialized) {
                      el.innerHTML = formData.content;
                      el.dataset.initialized = 'true';
                    }
                  }}
                  contentEditable
                  className="w-full min-h-[300px] max-h-[400px] overflow-y-auto p-4 border rounded-b-lg focus:ring-2 focus:ring-red-500 focus:outline-none blog-editor"
                  onInput={handleEditorChange}
                  onBlur={handleEditorChange}
                  style={{
                    lineHeight: '1.8',
                  }}
                />
                
                <p className="text-xs text-gray-500 mt-2">
                  💡 İpucu: Metni seçip araç çubuğundan formatla, veya direkt yazarak düzenle
                </p>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => handleChange('is_published', e.target.checked)}
                    className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Yayınla</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => handleChange('is_featured', e.target.checked)}
                    className="w-5 h-5 text-yellow-500 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm text-gray-700">Öne Çıkar</span>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'style' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value.toUpperCase())}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="REHBER, SINAV, MALİYET..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori Rengi
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {CATEGORY_COLORS.map(color => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => handleChange('category_color', color.value)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${color.bg} ${color.text} ${
                          formData.category_color === color.value 
                            ? 'ring-2 ring-offset-2 ring-gray-400' 
                            : ''
                        }`}
                      >
                        {color.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Free Emoji Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emoji (İstediğiniz emojiyi yapıştırın)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="text"
                    value={formData.emoji}
                    onChange={(e) => handleChange('emoji', e.target.value)}
                    className="w-24 px-4 py-3 text-center text-3xl border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="📝"
                  />
                  <span className="text-gray-500 text-sm">
                    İnternetten herhangi bir emoji kopyalayıp yapıştırabilirsiniz
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Örnek: 🚗 📝 🚦 💰 📖 🎯 📄 🤔 🇹🇷 🇸🇪 ✅ ⚠️ 📋 🔑 🎓 🏆 💡 🔥 ⭐ 🎉
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kapak Resmi
                </label>
                <div className="flex items-center gap-4">
                  {formData.image_url && (
                    <img 
                      src={formData.image_url.startsWith('/') ? `${API_URL}${formData.image_url}` : formData.image_url}
                      alt="Cover" 
                      className="w-32 h-20 object-cover rounded-lg"
                    />
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors">
                    <Upload className="w-5 h-5" />
                    {uploading ? 'Yükleniyor...' : 'Resim Yükle'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {formData.image_url && (
                    <button
                      type="button"
                      onClick={() => handleChange('image_url', '')}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Kaldır
                    </button>
                  )}
                </div>
              </div>

              {/* Live Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Canlı Önizleme
                </label>
                <div className={`bg-gradient-to-r ${formData.gradient} p-6 rounded-xl`}>
                  <span className="text-5xl">{formData.emoji}</span>
                  <h3 className="text-xl font-bold text-white mt-3">{formData.title || 'Başlık'}</h3>
                  <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm text-white">
                    {formData.category || 'Kategori'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Başlık (SEO)
                </label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Arama motorlarında görünecek başlık (boş bırakılırsa ana başlık kullanılır)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.meta_title?.length || 0}/60 karakter (önerilen)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Açıklama (SEO)
                </label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Arama motorlarında görünecek açıklama..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.meta_description?.length || 0}/160 karakter (önerilen)
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Google Önizleme:</p>
                <div className="bg-white p-3 rounded border">
                  <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                    {formData.meta_title || formData.title || 'Sayfa Başlığı'}
                  </p>
                  <p className="text-green-700 text-sm">
                    ehliyet.nu/blog/{formData.slug || 'url-slug'}
                  </p>
                  <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                    {formData.meta_description || formData.excerpt || 'Sayfa açıklaması burada görünecek...'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Save className="w-5 h-5" />
            {post?.id ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </div>

      {/* Editor Styles */}
      <style>{`
        .blog-editor {
          font-family: inherit;
        }
        .blog-editor h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin: 1rem 0 0.5rem 0;
        }
        .blog-editor h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0.75rem 0 0.5rem 0;
        }
        .blog-editor p {
          color: #374151;
          margin: 0.5rem 0;
        }
        .blog-editor ul, .blog-editor ol {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }
        .blog-editor li {
          color: #374151;
          margin: 0.25rem 0;
        }
        .blog-editor strong {
          font-weight: 600;
        }
        .blog-editor a {
          color: #dc2626;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
